/**
 * Title screen visuals: calligraphic 南天传说 (live text, FONT_TITLE stack with stroke + shadow),
 * 第一章 · 南洋行, the integrator's ordered menu entries (继续旅程 when a save exists, 新的旅程) plus
 * 设置 (opens the UI settings panel), keyboard + pointer, and a dev placeholder haze / drizzle
 * animation behind (deterministic: SeededRng-driven tweens, no particle RNG).
 *
 * Called by TitleScene.create as `renderTitle(scene, { entries: [{ label, run }], onReady })`.
 */
import Phaser from 'phaser';
import { CHAPTER_TITLE, FONT_DISPLAY, FONT_TITLE, GAME_H, GAME_TITLE, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { SeededRng } from '@/core/rng';
import { describeSlot } from '@/save/labels';
import { ensureDevUiTextures } from '../devTextures';
import { MenuList } from '../MenuList';
import { uiText } from '../text';
import { Css, Palette } from '../theme';
import { uiRootOf } from '../UiRoot';
import type { UiComponents } from '../index';

import { buildTitleItems, type TitleEntry } from './items';
export { buildTitleItems, type TitleEntry } from './items';

export interface TitleOptions {
  /** Ordered menu entries from the integrator (继续旅程 first when a save exists, then 新的旅程). */
  entries?: TitleEntry[];
  /** Called once the menu is interactive (after the entrance fade). */
  onReady?: () => void;
  /** Fallback when the UI settings panel is unavailable (settings entry is hidden if neither exists). */
  onSettings?: () => void;
  /** Extra label for the 设置 entry (default 设置). */
  settingsLabel?: string;
  version?: string;
  /** Skip the fade-in (verification). */
  instant?: boolean;
}

export interface TitleHandle { destroy(): void; readonly menu: MenuList; select(i: number): void; readonly items: readonly import('../MenuList').MenuItem[] }

function uiOf(scene: Phaser.Scene): UiComponents | null {
  const ui = scene.scene.get('UI') as (Phaser.Scene & { uiRoot?: unknown; dialogueBox?: unknown }) | null;
  if (!ui || !ui.uiRoot) return null;
  return ui as unknown as UiComponents;
}

declare const __GAME_VERSION__: string | undefined;
function gameVersion(): string {
  try { return typeof __GAME_VERSION__ === 'string' ? __GAME_VERSION__ : ''; } catch { return ''; }
}

export function renderTitle(scene: Phaser.Scene, opts: TitleOptions = {}): TitleHandle {
  ensureDevUiTextures(scene);
  const ctx = ctxOf(scene);
  const reduce = ctx?.settings.reduceMotion ?? false;
  const rng = new SeededRng('title-weather');
  const objects: Phaser.GameObjects.GameObject[] = [];
  const keep = <T extends Phaser.GameObjects.GameObject>(o: T): T => { objects.push(o); return o; };

  scene.cameras.main.setBackgroundColor(Palette.night);

  // ---- backdrop (dev placeholder: sultry pre-rain harbour in colour bands)
  const skyTop = Phaser.Display.Color.ValueToColor(0x1b2130);
  const skyHorizon = Phaser.Display.Color.ValueToColor(0x4a4f67);
  for (let i = 0; i < 10; i++) {
    const c = Phaser.Display.Color.Interpolate.ColorWithColor(skyTop, skyHorizon, 9, i);
    keep(scene.add.rectangle(0, i * 20, GAME_W, 20, Phaser.Display.Color.GetColor(c.r, c.g, c.b)).setOrigin(0));
  }
  const seaTop = Phaser.Display.Color.ValueToColor(0x2b5f66);
  const seaBottom = Phaser.Display.Color.ValueToColor(0x0f1a24);
  for (let i = 0; i < 8; i++) {
    const c = Phaser.Display.Color.Interpolate.ColorWithColor(seaTop, seaBottom, 7, i);
    keep(scene.add.rectangle(0, 200 + i * 20, GAME_W, 20, Phaser.Display.Color.GetColor(c.r, c.g, c.b)).setOrigin(0));
  }
  // distant piers / hulls silhouettes
  const silhouette = 0x121826;
  for (const [x, w, h] of [[36, 90, 14], [150, 40, 22], [420, 120, 12], [560, 60, 26]] as const) {
    keep(scene.add.rectangle(x, 200 - h, w, h, silhouette).setOrigin(0));
    for (let px = x + 6; px < x + w; px += 14) keep(scene.add.rectangle(px, 200, 3, 16, silhouette).setOrigin(0));
  }
  keep(scene.add.triangle(250, 200, 0, 0, 70, 0, 60, -30, silhouette).setOrigin(0, 0)); // beached junk bow
  keep(scene.add.rectangle(300, 150, 3, 50, silhouette).setOrigin(0)); // mast
  // turtle-shell inn dome far right
  keep(scene.add.ellipse(600, 200, 90, 40, silhouette).setOrigin(0.5, 1));
  // a few warm lantern points on the far shore
  for (const [lx, ly] of [[80, 190], [172, 184], [470, 192], [590, 178]] as const) {
    const dot = keep(scene.add.image(lx, ly, 'ui-dot').setTint(Palette.lantern).setAlpha(0.7).setScale(0.6));
    if (!reduce) scene.tweens.add({ targets: dot, alpha: 0.35, duration: rng.int(900, 1600), yoyo: true, repeat: -1, ease: 'Sine.easeInOut', delay: rng.int(0, 800) });
  }

  // ---- haze blobs
  const hazeLayer = keep(scene.add.container(0, 0));
  for (let i = 0; i < 10; i++) {
    const img = scene.add.image(rng.int(0, GAME_W), rng.int(120, 250), 'ui-haze').setScale(rng.range(3, 5)).setAlpha(rng.range(0.25, 0.5)).setTint(0xdfe6ee);
    hazeLayer.add(img);
    if (!reduce) scene.tweens.add({ targets: img, x: img.x + rng.int(-30, 30), duration: rng.int(6000, 11000), yoyo: true, repeat: -1, ease: 'Sine.easeInOut', delay: rng.int(0, 2000) });
  }
  // ---- drizzle streaks
  const rainLayer = keep(scene.add.container(0, 0)).setAlpha(0.35);
  if (!reduce) {
    for (let i = 0; i < 36; i++) {
      const img = scene.add.image(rng.int(0, GAME_W), rng.int(-360, 0), 'ui-rain').setTint(0xbfd0e0).setAlpha(rng.range(0.4, 0.9));
      rainLayer.add(img);
      const dur = rng.int(1400, 2200);
      scene.tweens.add({ targets: img, y: GAME_H + 12, x: img.x - 14, duration: dur, repeat: -1, delay: rng.int(0, dur), onRepeat: () => { img.y = -12; img.x = rng.int(0, GAME_W); } });
    }
  }

  // ---- title block (live text; FONT_DISPLAY = Nantian Display → Nantian Serif → system serif)
  const titleGroup = keep(scene.add.container(0, 0));
  const title = uiText(scene, GAME_W / 2, 100, GAME_TITLE, {
    fontFamily: FONT_DISPLAY, fontSize: '58px', color: Css.sand, stroke: Css.lacquerDark, strokeThickness: 6,
    shadow: { offsetX: 3, offsetY: 4, color: '#000000', blur: 0, fill: true, stroke: true }, letterSpacing: 6,
  }).setOrigin(0.5);
  const rule1 = scene.add.rectangle(GAME_W / 2, 140, 220, 1, Palette.sand, 0.6).setOrigin(0.5);
  const subtitle = uiText(scene, GAME_W / 2, 156, `第一章 · ${CHAPTER_TITLE}`, { fontFamily: FONT_TITLE, fontSize: '18px', fontStyle: 'bold', color: Css.parchment, stroke: '#000000', strokeThickness: 3 }).setOrigin(0.5);
  const rule2 = scene.add.rectangle(GAME_W / 2, 172, 220, 1, Palette.sand, 0.6).setOrigin(0.5);
  const seal = scene.add.rectangle(GAME_W / 2 + 130, 100, 14, 14, Palette.lacquer, 0.9).setOrigin(0.5);
  const sealMark = uiText(scene, GAME_W / 2 + 130, 100, '南', { fontFamily: FONT_TITLE, fontSize: '10px', fontStyle: 'bold', color: Css.parchment }).setOrigin(0.5);
  titleGroup.add([title, rule1, subtitle, rule2, seal, sealMark]);

  // ---- menu
  const latest = ctx?.saves.latest() ?? null;
  const isBlocked = () => Boolean(uiRootOf(scene)?.hasModal());
  const openSettings = (): boolean => {
    const pm = uiOf(scene)?.pauseMenu;
    if (pm) return pm.openSettings();
    if (opts.onSettings) { opts.onSettings(); return true; }
    return false;
  };
  const hasSettings = Boolean(uiOf(scene)?.pauseMenu) || Boolean(opts.onSettings);
  const buildOpts: Parameters<typeof buildTitleItems>[1] = {};
  if (latest) buildOpts.continueHint = describeSlot(latest.slot, latest.data).beat;
  if (hasSettings) buildOpts.settings = () => { openSettings(); };
  if (opts.settingsLabel) buildOpts.settingsLabel = opts.settingsLabel;
  const items = buildTitleItems(opts.entries ?? [], buildOpts);
  const menu = new MenuList(scene, { x: GAME_W / 2, y: 216, width: 200, rowHeight: 28, fontSize: 17, items, isBlocked, depth: 5, color: Css.parchment, selectedColor: Css.white });

  keep(uiText(scene, 8, GAME_H - 8, '↑↓ 选择 · Enter 确定', { fontSize: '10px', color: Css.dim }).setOrigin(0, 1));
  const version = opts.version ?? gameVersion();
  keep(uiText(scene, GAME_W - 6, GAME_H - 6, `${version ? `v${version}  ` : ''}DEV BACKDROP`, { fontSize: '10px', color: Css.dim }).setOrigin(1));

  // ---- entrance
  let readyFired = false;
  const fireReady = () => { if (readyFired) return; readyFired = true; opts.onReady?.(); };
  if (!reduce && !opts.instant) {
    titleGroup.setAlpha(0);
    menu.container.setAlpha(0);
    scene.tweens.add({ targets: titleGroup, alpha: 1, duration: 700, ease: 'Sine.easeOut' });
    scene.tweens.add({ targets: menu.container, alpha: 1, duration: 500, delay: 350, onComplete: fireReady });
    // Safety: never leave the harness waiting if tweens are paused (e.g. hidden tab).
    scene.time.delayedCall(1200, fireReady);
  } else {
    scene.time.delayedCall(0, fireReady);
  }

  const handle: TitleHandle = {
    menu,
    items,
    select: (i) => menu.select(i),
    destroy: () => {
      menu.destroy();
      for (const o of objects) o.destroy();
      objects.length = 0;
    },
  };
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => handle.destroy());
  return handle;
}
